/**
 * App-side gate for the flyways filter perf lab.
 * Heavy modules live under `tests/perf/` and load only when
 * `NEXT_PUBLIC_ENABLE_PERF_LAB=true` (false by default).
 */

import type { FlywaysMapFilterId } from '@/constants/flywaysMap';

type FlywaysPerfMarksApi = {
  isFlywaysPerfEnabled: () => boolean;
  beginFlywaysFilterTransition: (meta: {
    from: FlywaysMapFilterId;
    to: FlywaysMapFilterId;
    compact: boolean;
    reducedMotion: boolean;
  }) => void;
  markFlywaysFilterGsapReady: () => void;
  markFlywaysFilterFadeOutStart: () => void;
  markFlywaysFilterCameraStart: () => void;
  markFlywaysFilterVisibilitySwap: () => void;
  markFlywaysFilterFadeInStart: () => void;
  markFlywaysFilterCometSync: () => void;
  endFlywaysFilterTransition: () => unknown;
};

const NOOP_MARKS: FlywaysPerfMarksApi = {
  isFlywaysPerfEnabled: () => false,
  beginFlywaysFilterTransition: () => undefined,
  markFlywaysFilterGsapReady: () => undefined,
  markFlywaysFilterFadeOutStart: () => undefined,
  markFlywaysFilterCameraStart: () => undefined,
  markFlywaysFilterVisibilitySwap: () => undefined,
  markFlywaysFilterFadeInStart: () => undefined,
  markFlywaysFilterCometSync: () => undefined,
  endFlywaysFilterTransition: () => null,
};

function isPerfLabEnvEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_PERF_LAB === 'true';
}

let marksPromise: Promise<FlywaysPerfMarksApi> | null = null;

export function isFlywaysPerfLabEnabled(): boolean {
  return isPerfLabEnvEnabled();
}

export function loadFlywaysPerfMarks(): Promise<FlywaysPerfMarksApi> {
  if (!isPerfLabEnvEnabled()) {
    return Promise.resolve(NOOP_MARKS);
  }
  marksPromise ??= import('@perf/flyways-filter/flywaysFilterMarks').then(
    (mod) => ({
      isFlywaysPerfEnabled: mod.isFlywaysPerfEnabled,
      beginFlywaysFilterTransition: mod.beginFlywaysFilterTransition,
      markFlywaysFilterGsapReady: mod.markFlywaysFilterGsapReady,
      markFlywaysFilterFadeOutStart: mod.markFlywaysFilterFadeOutStart,
      markFlywaysFilterCameraStart: mod.markFlywaysFilterCameraStart,
      markFlywaysFilterVisibilitySwap: mod.markFlywaysFilterVisibilitySwap,
      markFlywaysFilterFadeInStart: mod.markFlywaysFilterFadeInStart,
      markFlywaysFilterCometSync: mod.markFlywaysFilterCometSync,
      endFlywaysFilterTransition: mod.endFlywaysFilterTransition,
    }),
  );
  return marksPromise;
}

export function installFlywaysPerfBenchWhenEnabled(): void {
  if (!isPerfLabEnvEnabled()) return;
  void import('@perf/flyways-filter/flywaysFilterBench').then((mod) => {
    mod.installFlywaysFilterBench();
  });
}

export function installHeroSwirlPerfBenchWhenEnabled(): void {
  if (!isPerfLabEnvEnabled()) return;
  void import('@perf/hero-swirl/heroSwirlBench').then((mod) => {
    mod.installHeroSwirlBench();
  });
}

type HeroSwirlPerfMarksApi = {
  isHeroSwirlPerfEnabled: () => boolean;
  recordHeroSwirlSync: (sample: {
    durationMs: number;
    layoutReads: number;
    transformWrites: number;
  }) => void;
  recordHeroSwirlClipStyleWrites: (count: number) => void;
};

const NOOP_HERO_SWIRL_MARKS: HeroSwirlPerfMarksApi = {
  isHeroSwirlPerfEnabled: () => false,
  recordHeroSwirlSync: () => undefined,
  recordHeroSwirlClipStyleWrites: () => undefined,
};

let heroSwirlMarksPromise: Promise<HeroSwirlPerfMarksApi> | null = null;

export function loadHeroSwirlPerfMarks(): Promise<HeroSwirlPerfMarksApi> {
  if (!isPerfLabEnvEnabled()) {
    return Promise.resolve(NOOP_HERO_SWIRL_MARKS);
  }
  heroSwirlMarksPromise ??= import('@perf/hero-swirl/heroSwirlMarks').then(
    (mod) => ({
      isHeroSwirlPerfEnabled: mod.isHeroSwirlPerfEnabled,
      recordHeroSwirlSync: mod.recordHeroSwirlSync,
      recordHeroSwirlClipStyleWrites: mod.recordHeroSwirlClipStyleWrites,
    }),
  );
  return heroSwirlMarksPromise;
}
